import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { getScreenerData } from '@/lib/api'
import { boundHistoryRange, bufferedHistoryRange, historyDomain, historyWindowCovers, initialHistoryRange,
  type ChartTimeRange } from '@/lib/screenerHistory'
import type { ScreenerTickerData, ScreenerTimeframe } from '@/types'

/** A bounded buffer around the viewport. Polling snapshots never evict the window being inspected. */
export function useScreenerHistory(symbol: string, liveData: ScreenerTickerData | null,
  interval: string, timeframe: ScreenerTimeframe | undefined, exchanges: string[], includeOIOhlc = false) {
  const [selection, setSelection] = useState<ChartTimeRange | null>(() =>
    timeframe?.mode === 'absolute' ? initialHistoryRange(liveData, interval, timeframe) : null)
  const [snapshot, setSnapshot] = useState<ScreenerTickerData | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [retry, setRetry] = useState(0)
  const loadedRef = useRef<ChartTimeRange | null>(null)
  const loadedOhlcRef = useRef(includeOIOhlc)
  const userNavigatedRef = useRef(false)
  const resolutionRef = useRef<{ interval: string; span: number } | null>(null)
  const exchangeKey = [...exchanges].sort().join(',')
  const range = selection || initialHistoryRange(liveData, interval, timeframe)
  const domain = historyDomain(range, snapshot?.history_window?.earliest_ts)
  const initialDataReady = liveData !== null

  useEffect(() => {
    if (loadedOhlcRef.current !== includeOIOhlc) {
      loadedOhlcRef.current = includeOIOhlc
      loadedRef.current = null
      resolutionRef.current = null
      setSnapshot(null)
    }
    if (!selection) return
    // The initial full request already loads the saved viewport and its buffer.
    // Wait for it before deciding whether navigating requires another window.
    if (!initialDataReady) return
    const initialWindow = liveData?.history_window
    if (!loadedRef.current && (liveData?.oi_include_ohlc ?? false) === includeOIOhlc
      && initialWindow && !initialWindow.truncated
      && historyWindowCovers({ from: initialWindow.from_ts, to: initialWindow.to_ts }, selection)) {
      loadedRef.current = { from: initialWindow.from_ts, to: initialWindow.to_ts }
      resolutionRef.current = { interval: liveData.oi_interval || interval, span: selection.to - selection.from }
      if (userNavigatedRef.current) setSnapshot(liveData)
    }
    const resolution = resolutionRef.current
    const needsFinerData = resolution && resolution.interval !== interval
      && selection.to - selection.from < resolution.span / 2
    if (!needsFinerData && loadedRef.current && historyWindowCovers(loadedRef.current, selection)) {
      setError(null)
      return
    }
    const controller = new AbortController()
    const requested = bufferedHistoryRange(selection)
    const timer = setTimeout(async () => {
      setLoading(true)
      setError(null)
      try {
        const result = await getScreenerData(symbol, { mode: 'absolute', from_ts: requested.from, to_ts: requested.to },
          interval, false, controller.signal, exchangeKey ? exchangeKey.split(',') : [], requested, includeOIOhlc)
        if (controller.signal.aborted) return
        if (!result.ok || !result.data) throw new Error(result.error || 'Unable to load chart history')
        const window = result.data.history_window
        if (window?.truncated) throw new Error('This range contains too many points. Choose a larger interval or a shorter range.')
        loadedRef.current = window
          ? { from: window.from_ts, to: window.to_ts } : requested
        resolutionRef.current = { interval: result.data.oi_interval || interval, span: selection.to - selection.from }
        setSnapshot(result.data)
      } catch (cause) {
        if (!controller.signal.aborted) setError(cause instanceof Error ? cause.message : 'Unable to load chart history')
      } finally {
        if (!controller.signal.aborted) setLoading(false)
      }
    }, 180)
    return () => { clearTimeout(timer); controller.abort(); setLoading(false) }
  }, [selection, symbol, interval, exchangeKey, includeOIOhlc, retry, initialDataReady])

  const navigate = useCallback((next: ChartTimeRange) => {
    if (!Number.isFinite(next.from) || !Number.isFinite(next.to) || next.to <= next.from) return
    next = boundHistoryRange(next, interval)
    userNavigatedRef.current = true
    setSnapshot(current => current ?? liveData)
    setSelection(current => current && Math.abs(current.from - next.from) < 0.01
      && Math.abs(current.to - next.to) < 0.01 ? current : next)
  }, [liveData, interval])
  const goLive = useCallback(() => {
    setSelection(null)
    setSnapshot(null)
    setError(null)
    loadedRef.current = null
    userNavigatedRef.current = false
    resolutionRef.current = null
  }, [])
  const { from, to } = range
  const { from: domainFrom, to: domainTo } = domain
  const navigation = useMemo(() => ({ range: { from, to }, domain: { from: domainFrom, to: domainTo }, navigate }),
    [from, to, domainFrom, domainTo, navigate])
  return { data: selection && snapshot ? snapshot : liveData, navigation,
    browsing: selection !== null, loading, error, goLive, retry: () => setRetry(value => value + 1) }
}
